import React from "react";

import { Picto } from "@amphore-dev/amphore-lib";
import { t } from "i18next";

import { useAuth } from "@/hooks/useAuth";
import { useIsRouteActive } from "@/hooks/useIsRouteActive";
import { useNewTask } from "@/hooks/useNewTask";
import { useSubscription } from "@/hooks/useSubscription";

import { Avatar } from "@/components/atoms";
import { NavBarItem } from "@/components/molecules";
import { bannerFor } from "@/components/organisms/SubscriptionBanner/SubscriptionBanner";

import { cn } from "@/utils/cn";

import { ILayoutMenuProps } from "@/types";

import { ME_PATHS } from "@/router/NavRoutes";

/**
 * The mobile bottom bar: the routes' tabs split around the new-task
 * button, then the "Me" tab, which gathers everything else.
 */
export const NavBar: React.FC<ILayoutMenuProps> = ({
	routes,
	className,
	style,
}) => {
	const { hasRole } = useAuth();

	const tabs = (routes ?? []).flatMap((route) => {
		if (route.hidden) return [];
		if (route.roles && !hasRole(route.roles, route.needAllRoles)) return [];
		const path = Array.isArray(route.path) ? route.path[0] : route.path;
		if (!path) return [];
		return [
			<NavBarItem
				{...route}
				key={path}
				name={route.name ?? ""}
				path={route.path ?? path}
				title={typeof route.title === "string" ? route.title : ""}
				to={path}
			>
				{typeof route.title === "string" ? t(route.title) : route.title}
			</NavBarItem>,
		];
	});
	const middle = Math.ceil(tabs.length / 2);

	return (
		<nav
			aria-label={t("nav.main")}
			data-tour="menu"
			className={cn([
				"z-1 flex w-full shrink-0 flex-row items-center border-t border-neutral-200 bg-white px-2 pt-1.5 pb-[max(0.375rem,env(safe-area-inset-bottom))]",
				className,
			])}
			style={style}
		>
			{tabs.slice(0, middle)}
			<NewTaskTab />
			{tabs.slice(middle)}
			<MeTab />
		</nav>
	);
};

/** In line with the tabs, not floating: a filled square, no label. */
const NewTaskTab: React.FC = () => {
	const { canCreate, create } = useNewTask();
	if (!canCreate) return null;
	return (
		<div className="flex flex-1 justify-center">
			<button
				type="button"
				aria-label={t("tasks.newTask")}
				title={t("tasks.newTask")}
				data-tour="new-task"
				onClick={create}
				className="bg-primary-700 hover:bg-primary-800 flex h-11 w-11 items-center justify-center rounded-xl text-white shadow-sm"
			>
				<Picto icon="add" className="h-6 w-6" />
			</button>
		</div>
	);
};

const MeTab: React.FC = () => {
	const { user } = useAuth();
	const { subscription, plan } = useSubscription();
	const isActive = useIsRouteActive(ME_PATHS);
	// Same rule as the banner: the dot says the subscription wants a look.
	const hasNotice = !!bannerFor(subscription, plan?.code ?? "free");

	return (
		<NavBarItem
			name="me"
			to="/me"
			path={ME_PATHS}
			badge={hasNotice}
			icon={
				<Avatar
					url={user?.avatar}
					className={cn([
						"h-7! w-7!",
						isActive && "ring-primary-700 ring-2 ring-offset-1",
					])}
				/>
			}
		>
			{t("me.title")}
		</NavBarItem>
	);
};
